import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { EventForm } from "Bands/components";
import {
  useBandContext,
  useEventContext,
  usePermissionsContext,
} from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import {
  clearActionMeta,
  clearDataByKey,
  dataMetaSelector,
  dispatchGetEventScheduleBlocks,
  dispatchGetEventTravelBlocks,
  editEvent,
  eventBlockSelectorCreator,
} from "Bands/state";
import {
  BAND_ACTIONS,
  BandSettingsSlugs,
  EventBlocks,
  EventUniqueBlocks,
  IEvent,
  IEventPeopleBlock,
} from "Bands/types";
import { useEventRoute } from "Bands/utils";
import { copy } from "shared/assets";
import { Bin } from "shared/assets/icons";
import { FancyButtonLink } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { Icon } from "shared/styles";
import { colors } from "shared/styles/constants";
import { IAPIState, IDataKeys, IStatus } from "shared/types";
import { ChangedDateModal } from "./ChangedDateModal";
import { dispatchGetEventAccommodationBlocks } from "Bands/state/data/actions/eventBlocks/eventAccommodationBlock";

const editCopy = copy.event.edit;
const deleteCopy = copy.event.delete;

interface EditEventFormProps {
  placeholder: Partial<IEvent>;
  updatePlaceholder: (event: Partial<IEvent>) => void;
  canDeleteEvent?: boolean;
}

const possibleBlockTypeOptions = [
  {
    label: "Schedule dates",
    value: EventBlocks.schedule,
  },
  {
    label: "Travel dates",
    value: EventBlocks.travel,
  },
  {
    label: "Accommodation dates",
    value: EventBlocks.accommodation,
  },
];

export const EditEventForm: FC<EditEventFormProps> = ({
  placeholder,
  updatePlaceholder,
  canDeleteEvent,
  ...props
}) => {
  const { userCanManageBand } = usePermissionsContext();
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editEvent)
  );
  const { pushRoute } = useEventRoute();
  const { event, openDeleteModal, eventBlocks } = useEventContext();
  const { eventTypes } = useBandContext();

  const peopleBlockId = useMemo(
    () =>
      eventBlocks?.find(
        (eventBlock) => eventBlock.type === EventUniqueBlocks.people
      )?.id,
    [eventBlocks]
  );

  const peopleBlockSelector = eventBlockSelectorCreator(
    peopleBlockId || "",
    EventUniqueBlocks.people
  );

  const peopleBlock = useSelector(peopleBlockSelector) as IEventPeopleBlock;

  const [showChangedDateModal, setShowChangedDateModal] =
    useState<Partial<IEvent>>();

  const blockTypesToUpdateOptions = useMemo(
    () =>
      possibleBlockTypeOptions.filter((blockType) =>
        eventBlocks?.find((block) => block.type === blockType.value)
      ),
    [eventBlocks]
  );

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.editEvent));

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.success,
        })
      );

      dispatch(clearDataByKey(IDataKeys.eventScheduleBlock));
      dispatch(clearDataByKey(IDataKeys.eventTravelBlock));
      dispatch(clearDataByKey(IDataKeys.eventAccommodationBlock));

      dispatchGetEventScheduleBlocks(dispatch, event?.id);
      dispatchGetEventTravelBlocks(dispatch, event?.id);
      dispatchGetEventAccommodationBlocks(dispatch, event?.id);

      pushRoute(BAND_PATHS.BAND_EVENT);
    }
  }, [apiMeta, pushRoute, dispatch, updatePlaceholder, event?.id]);

  const defaultValues = useMemo(() => {
    if (event) {
      return {
        ...event,
        type:
          typeof event.type !== "string"
            ? API_ENDPOINTS.EVENT_SETTING_ITEM(
                BandSettingsSlugs.event_types,
                event.type.id
              )
            : event.type,
      };
    }
    return {};
  }, [event]);

  const handleSubmit = (data: Partial<IEvent>) => {
    if (
      event?.date !== data.date &&
      (!!blockTypesToUpdateOptions.length || hasRSVPs)
    ) {
      setShowChangedDateModal(data);
    } else {
      event && dispatch(editEvent({ data, id: event.id }));
    }
  };

  const hasRSVPs = useMemo(
    () => !!peopleBlock?.users?.filter((user) => user.rsvp)?.length,
    [peopleBlock?.users]
  );

  return (
    <Spacer size="l">
      {showChangedDateModal && (
        <ChangedDateModal
          submittedEventData={showChangedDateModal}
          setShowChangedDateModal={setShowChangedDateModal}
          blockTypesToUpdateOptions={blockTypesToUpdateOptions}
          hasRSVPs={hasRSVPs}
        />
      )}
      <EventForm
        hasError={!!apiMeta?.error}
        apiStatus={apiMeta?.status}
        handleSubmit={handleSubmit}
        placeholder={placeholder}
        updatePlaceholder={updatePlaceholder}
        buttons={{ primary: true }}
        defaultValues={defaultValues}
        eventTypes={eventTypes}
        apiError={apiMeta?.error}
        canManageTypes={userCanManageBand}
        {...props}
      />
      {canDeleteEvent && (
        <FancyButtonLink
          status={IStatus.error}
          onClick={openDeleteModal}
          data-testid="delete_event_button"
        >
          <Spacer spaceDirection="horizontal" size="xxs" align="center">
            <Icon as={Bin} color={colors.red} size="l" />
            <span>{deleteCopy.cta}</span>
          </Spacer>
        </FancyButtonLink>
      )}
    </Spacer>
  );
};
